// pages/SalesReports.jsx
import React, { useState, useMemo } from 'react';
import {
  Loader2, RefreshCw, Filter, TrendingUp,
  Users, Calendar, Award, IndianRupee, Radio, GitBranch,
} from 'lucide-react';
import { Container, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import SideNav from '../components/SideNav';
import { useSalesData } from '../hooks/useSalesData';

const ALL_REPS     = { value: 'allReps',     label: 'All Reps'     };
const ALL_CITIES   = { value: 'allCities',   label: 'All Cities'   };
const ALL_SOURCES  = { value: 'allSources',  label: 'All Sources'  };
const ALL_BRANCHES = { value: 'allBranches', label: 'All Branches' };

const ALL_SENTINELS = new Set(['allReps','allCities','allSources','allBranches']);
const isAllOption   = (v) => ALL_SENTINELS.has(v?.value);

const MARKETING_SOURCES = new Set([
  'facebook','fb','ig','instagram','bark','google','meta', 'quora'
]);
const normalizeSource = (s) =>
  MARKETING_SOURCES.has(s?.toLowerCase()) ? 'Marketing' : s;

const MARKETING_RAW = ['facebook','fb','ig','instagram','bark','google','meta'];
const expandSources = (sources) =>
  sources.flatMap(s => s === 'Marketing' ? MARKETING_RAW : [s]);

const selectStyles = {
  control: (p, s) => ({
    ...p,
    borderColor: s.isFocused ? '#0d6efd' : '#ced4da',
    boxShadow:   s.isFocused ? '0 0 0 0.2rem rgba(13,110,253,.25)' : 'none',
    '&:hover':   { borderColor: '#0d6efd' },
    minHeight:   38,
  }),
  multiValue:       (p) => ({ ...p, backgroundColor: '#e7f1ff', borderRadius: 4 }),
  multiValueLabel:  (p) => ({ ...p, color: '#0d6efd', fontWeight: 500 }),
  multiValueRemove: (p) => ({
    ...p, color: '#0d6efd',
    ':hover': { backgroundColor: '#0d6efd', color: 'white' },
  }),
};

const fmt = (v) => `₹${Math.round(v).toLocaleString('en-IN')}`;

const makeOnChange = (setter) => (val) => {
  if (!val || val.length === 0) return;
  const last = val[val.length - 1];
  if (isAllOption(last)) { setter([last]); return; }
  setter(val.filter(v => !isAllOption(v)));
};

const currentYearMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const SalesReports = () => {

  const [selectedReps,     setSelectedReps]     = useState([ALL_REPS]);
  const [selectedCities,   setSelectedCities]   = useState([ALL_CITIES]);
  const [selectedSources,  setSelectedSources]  = useState([ALL_SOURCES]);
  const [selectedBranches, setSelectedBranches] = useState([ALL_BRANCHES]);
  const [dateFrom,         setDateFrom]         = useState(currentYearMonth);
  const [dateTo,           setDateTo]           = useState(currentYearMonth);

  // Expand Marketing back to raw source names before passing to the hook
  const expandedSources = useMemo(() => {
    if (selectedSources.every(isAllOption)) return selectedSources;
    return expandSources(selectedSources.map(s => s.value)).map(s => ({ value: s, label: s }));
  }, [selectedSources]);

  const filters = useMemo(() => ({
    reps:     selectedReps,
    cities:   selectedCities,
    sources:  expandedSources,
    branches: selectedBranches,
    dateFrom,
    dateTo,
  }), [selectedReps, selectedCities, expandedSources, selectedBranches, dateFrom, dateTo]);

  const {
    summaryStats, summaryLoading, summaryError, refetchSummary,
    filterOptions, optionsLoading,
    isLive,
  } = useSalesData({ filters });

  const repOptions = useMemo(() => [
    ALL_REPS,
    ...(filterOptions?.reps ?? []).map(r => ({ value: r, label: r })),
  ], [filterOptions]);

  const cityOptions = useMemo(() => [
    ALL_CITIES,
    ...(filterOptions?.cities ?? []).map(c => ({ value: c, label: c })),
  ], [filterOptions]);

  const sourceOptions = useMemo(() => {
    const seen = new Set();
    const opts = (filterOptions?.sources ?? [])
      .map(s => normalizeSource(s))
      .filter(s => { if (seen.has(s)) return false; seen.add(s); return true; })
      .map(s => ({ value: s, label: s }));
    return [ALL_SOURCES, ...opts];
  }, [filterOptions]);

  const branchOptions = useMemo(() => [
    ALL_BRANCHES,
    ...(filterOptions?.branches ?? []).map(b => ({ value: b, label: b })),
  ], [filterOptions]);

  const salesPerRep = useMemo(() => {
    const byRep = summaryStats?.byRep ?? {};
    return Object.entries(byRep)
      .map(([rep, d]) => ({ rep, total: d.value, count: d.count }))
      .sort((a, b) => b.total - a.total);
  }, [summaryStats]);

  const salesPerSource = useMemo(() => {
    const bySource = summaryStats?.bySource ?? {};
    const merged = {};
    Object.entries(bySource).forEach(([source, d]) => {
      const key = normalizeSource(source);
      if (!merged[key]) merged[key] = { total: 0, count: 0 };
      merged[key].total += d.value;
      merged[key].count += d.count;
    });
    return Object.entries(merged)
      .map(([source, d]) => ({ source, total: d.total, count: d.count }))
      .sort((a, b) => b.total - a.total);
  }, [summaryStats]);

  const salesPerBranch = useMemo(() => {
    const byBranch = summaryStats?.byBranch ?? {};
    return Object.entries(byBranch)
      .map(([branch, d]) => ({ branch, total: d.value, count: d.count }))
      .sort((a, b) => b.total - a.total);
  }, [summaryStats]);

  const topRep = salesPerRep[0] ?? null;

  const clearFilters = () => {
    setSelectedReps([ALL_REPS]);
    setSelectedCities([ALL_CITIES]);
    setSelectedSources([ALL_SOURCES]);
    setSelectedBranches([ALL_BRANCHES]);
    setDateFrom(currentYearMonth());
    setDateTo(currentYearMonth());
  };

  const hasActiveFilters =
    !selectedReps.every(isAllOption)     ||
    !selectedCities.every(isAllOption)   ||
    !selectedSources.every(isAllOption)  ||
    !selectedBranches.every(isAllOption);

  if (optionsLoading) return (
    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
      <Loader2 className="spin text-primary me-2" size={32} />
      <span className="text-muted">Loading dashboard...</span>
    </div>
  );

  return (
    <Container fluid className="p-3" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <Row>
        <Col className="col-auto"><SideNav /></Col>
        <Col style={{ padding: '0 10px 0 20px' }}>
          <div className="container-fluid py-4">
            <div className="container-xxl">

              {/* Header */}
              <div className="mb-4">
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
                  <div>
                    <h1 className="display-6 fw-bold text-dark mb-0">Sales Report</h1>
                    <small className={`d-flex align-items-center gap-1 mt-1 ${isLive ? 'text-success' : 'text-muted'}`}>
                      <Radio size={12} />
                      {isLive ? 'Live' : 'Connecting...'}
                    </small>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      onClick={refetchSummary}
                      className="btn btn-primary d-flex align-items-center"
                      disabled={summaryLoading}
                    >
                      <RefreshCw className={`me-2 ${summaryLoading ? 'spin' : ''}`} size={16} />
                      Refresh
                    </button>
                    {hasActiveFilters && (
                      <button onClick={clearFilters} className="btn btn-outline-secondary d-flex align-items-center">
                        <Filter className="me-2" size={16} />
                        Clear Filters
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Filters */}
              <div className="card mb-4">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <Filter className="me-2 text-muted" size={18} />
                    <h5 className="card-title mb-0">Filters</h5>
                    {summaryLoading && <Loader2 className="ms-3 spin text-primary" size={15} />}
                  </div>
                  <div className="row g-3">

                    <div className="col-12 col-md-2">
                      <label className="form-label fw-medium">From</label>
                      <input
                        type="month"
                        className="form-control"
                        value={dateFrom}
                        max={dateTo || undefined}
                        onChange={e => setDateFrom(e.target.value)}
                      />
                    </div>

                    <div className="col-12 col-md-2">
                      <label className="form-label fw-medium">To</label>
                      <input
                        type="month"
                        className="form-control"
                        value={dateTo}
                        min={dateFrom || undefined}
                        onChange={e => setDateTo(e.target.value)}
                      />
                    </div>

                    <div className="col-12 col-md-2">
                      <label className="form-label fw-medium">Sales Rep</label>
                      <Select
                        isMulti options={repOptions} value={selectedReps}
                        onChange={makeOnChange(setSelectedReps)} styles={selectStyles}
                        placeholder="All Reps" closeMenuOnSelect={false}
                        hideSelectedOptions={false} isClearable={false} isSearchable
                      />
                    </div>

                    <div className="col-12 col-md-2">
                      <label className="form-label fw-medium">City</label>
                      <Select
                        isMulti options={cityOptions} value={selectedCities}
                        onChange={makeOnChange(setSelectedCities)} styles={selectStyles}
                        placeholder="All Cities" closeMenuOnSelect={false}
                        hideSelectedOptions={false} isClearable={false} isSearchable
                      />
                    </div>

                    <div className="col-12 col-md-2">
                      <label className="form-label fw-medium">Branch</label>
                      <Select
                        isMulti options={branchOptions} value={selectedBranches}
                        onChange={makeOnChange(setSelectedBranches)} styles={selectStyles}
                        placeholder="All Branches" closeMenuOnSelect={false}
                        hideSelectedOptions={false} isClearable={false} isSearchable
                      />
                    </div>

                    <div className="col-12 col-md-2">
                      <label className="form-label fw-medium">Source</label>
                      <Select
                        isMulti options={sourceOptions} value={selectedSources}
                        onChange={makeOnChange(setSelectedSources)} styles={selectStyles}
                        placeholder="All Sources" closeMenuOnSelect={false}
                        hideSelectedOptions={false} isClearable={false} isSearchable
                      />
                    </div>

                  </div>
                </div>
              </div>

              {/* Summary cards */}
              <div className="row g-4 mb-4">
                {[
                  {
                    label:  'Total Closures',
                    value:  summaryLoading ? null : (summaryStats?.totalCount ?? 0),
                    icon:   <Users className="text-primary" size={32} />,
                    cls:    'text-primary',
                    format: v => v.toLocaleString('en-IN'),
                  },
                  {
                    label:  'Total Value',
                    value:  summaryLoading ? null : (summaryStats?.totalValue ?? 0),
                    icon:   <IndianRupee className="text-success" size={32} />,
                    cls:    'text-success',
                    format: fmt,
                  },
                  {
                    label:  'Avg Deal Size',
                    value:  summaryLoading ? null : (summaryStats?.avgDealSize ?? 0),
                    icon:   <TrendingUp className="text-info" size={32} />,
                    cls:    'text-info',
                    format: fmt,
                  },
                  {
                    label:    'Last Updated',
                    value:    summaryStats?.lastUpdated ?? null,
                    icon:     <Calendar className="text-muted" size={32} />,
                    cls:      'text-dark',
                    format:   v => new Date(v).toLocaleTimeString(),
                    fallback: new Date().toLocaleTimeString(),
                  },
                ].map(({ label, value, icon, cls, format, fallback }) => (
                  <div className="col-12 col-sm-6 col-lg-3" key={label}>
                    <div className="card h-100">
                      <div className="card-body d-flex justify-content-between align-items-center">
                        <div>
                          <p className="card-text text-muted small mb-1">{label}</p>
                          <h3 className={`card-title ${cls} mb-0`}>
                            {value === null
                              ? <Loader2 className="spin" size={24} />
                              : format(value)
                            }
                          </h3>
                        </div>
                        {icon}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Performance tables */}
              <div className="row g-4">

                <div className="col-12 col-lg-4">
                  <div className="card h-100">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">Rep Performance</h5>
                      {topRep && (
                        <span className="badge bg-warning text-dark">
                          <Award size={13} className="me-1" />{topRep.rep}
                        </span>
                      )}
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr><th>Rep</th><th>Value</th><th>Deals</th></tr>
                          </thead>
                          <tbody>
                            {salesPerRep.length === 0
                              ? <tr><td colSpan={3} className="text-center text-muted py-3">No data</td></tr>
                              : salesPerRep.map((r, i) => (
                                <tr key={r.rep}>
                                  <td className="fw-medium">
                                    {i === 0 && <Award className="text-warning me-1" size={14} />}
                                    {r.rep}
                                  </td>
                                  <td className="text-success fw-semibold">{fmt(r.total)}</td>
                                  <td>{r.count}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-lg-4">
                  <div className="card h-100">
                    <div className="card-header">
                      <h5 className="card-title mb-0">Source Performance</h5>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr><th>Source</th><th>Value</th><th>Deals</th></tr>
                          </thead>
                          <tbody>
                            {salesPerSource.length === 0
                              ? <tr><td colSpan={3} className="text-center text-muted py-3">No data</td></tr>
                              : salesPerSource.map((s, i) => (
                                <tr key={s.source}>
                                  <td className="fw-medium">
                                    {i === 0 && <TrendingUp className="text-success me-1" size={14} />}
                                    {s.source}
                                  </td>
                                  <td className="text-success fw-semibold">{fmt(s.total)}</td>
                                  <td>{s.count}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-lg-4">
                  <div className="card h-100">
                    <div className="card-header d-flex align-items-center gap-2">
                      <GitBranch size={16} className="text-muted" />
                      <h5 className="card-title mb-0">Branch Performance</h5>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                            <tr><th>Branch</th><th>Value</th><th>Deals</th></tr>
                          </thead>
                          <tbody>
                            {salesPerBranch.length === 0
                              ? <tr><td colSpan={3} className="text-center text-muted py-3">No data</td></tr>
                              : salesPerBranch.map((b, i) => (
                                <tr key={b.branch}>
                                  <td className="fw-medium">
                                    {i === 0 && <Award className="text-warning me-1" size={14} />}
                                    {b.branch}
                                  </td>
                                  <td className="text-success fw-semibold">{fmt(b.total)}</td>
                                  <td>{b.count}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>
        </Col>
      </Row>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .card { box-shadow: 0 .125rem .25rem rgba(0,0,0,.075); border: 1px solid rgba(0,0,0,.125); }
        .table th {
          border-bottom: 2px solid #dee2e6;
          font-size: .8rem;
          text-transform: uppercase;
          letter-spacing: .05em;
          white-space: nowrap;
        }
        input[type="month"] { height: 38px; }
      `}</style>
    </Container>
  );
};

export default SalesReports;